import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MailCheck } from "lucide-react";

import {
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  H2,
  Input,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
} from "@lagoon/ui";

import { api, type Invitation, type Member } from "../api.js";
import { Failure, LoadingPanel, Notice } from "../components/feedback.js";
import { SelectField } from "../components/select-field.js";
import {
  assignableRoles,
  canManageMembers,
  canManageOwners,
  parseOrgRole,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  type Org,
  type OrgRole,
} from "../orgs.js";

/**
 * Who is in this organisation.
 *
 * The screen is role-shaped: an owner or admin sees the pending invitations,
 * the invite form and the role controls; a member or a billing contact sees the
 * list and nothing to press. Both facts come from the same `role` the server
 * returned with the member list, never from a role stashed in the client.
 *
 * That gating is a courtesy, not a control. Every button here maps to a route
 * that re-decides for itself: `PATCH`/`DELETE` on a member resolve the caller's
 * role inside the transaction that makes the change, the pending list is
 * `admin` and above server-side, and a member who edits their own JavaScript to
 * show the invite form gets a 403 from the platform. The reason to hide them is
 * that offering somebody a button which will refuse them is a small lie.
 *
 * The one role rule that is not simply "admin and above": only an owner may
 * create or remove an owner. `assignableRoles` is where that lives, so the
 * dropdown an admin sees has no Owner option rather than having one that
 * always fails.
 */
export function Members(props: {
  org: Org;
  navigate: (to: string) => void;
  meOwnerId: string;
  onOrgCreated: (orgId: string) => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const orgId = props.org.id;

  const members = useQuery({
    queryKey: ["org-members", orgId],
    queryFn: () => api.listMembers(orgId),
  });

  // The caller's role as the server most recently reported it, falling back to
  // the switcher's copy until that arrives. Both come from the same table; the
  // member list's is the fresher of the two.
  const myRole: OrgRole = members.data?.role ?? props.org.role;
  const managing = canManageMembers(myRole);

  const invitations = useQuery({
    queryKey: ["org-invitations", orgId],
    queryFn: () => api.listInvitations(orgId),
    // The route is `admin` and above. Asking as a member would spend a request
    // to be told 403 and would paint an error box over a working screen.
    enabled: managing,
  });

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["org-members", orgId] }),
      queryClient.invalidateQueries({ queryKey: ["org-invitations", orgId] }),
      // Roles and memberships move seats, which the switcher shows.
      queryClient.invalidateQueries({ queryKey: ["orgs"] }),
    ]);
  };

  return (
    <div className="lg:flex lg:flex-col lg:gap-7">
      <PageHeader eyebrow={props.org.name} title="People" />

      {props.org.suspended ? (
        <Notice tone="danger">
          This organisation is suspended. Sites stay online; nothing new can be created until it is
          resolved.
        </Notice>
      ) : null}

      {managing ? <InviteForm orgId={orgId} onInvited={refresh} /> : null}

      <section className="lg:flex lg:flex-col lg:gap-4">
        <H2 className="lg:text-h3">
          Members{" "}
          {members.data === undefined ? null : (
            <span className="lg:font-normal lg:text-muted-foreground">
              · {members.data.seats} {members.data.seats === 1 ? "seat" : "seats"}
            </span>
          )}
        </H2>

        {members.isPending ? (
          <LoadingPanel label="Loading the members" />
        ) : members.isError ? (
          <Failure error={members.error} fallback="Could not load the members." />
        ) : (
          <MemberTable
            orgId={orgId}
            members={members.data.members}
            actorRole={myRole}
            meOwnerId={props.meOwnerId}
            onChanged={refresh}
            navigate={props.navigate}
          />
        )}

        {managing ? null : (
          <Text size="caption" tone="subtle">
            Only an owner or admin can invite people or change roles.
          </Text>
        )}
      </section>

      {managing ? (
        <PendingInvitations orgId={orgId} query={invitations} onChanged={refresh} />
      ) : null}

      <NewOrganisation onCreated={props.onOrgCreated} />
    </div>
  );
}

/** The member list, with the controls a managing role gets and nobody else. */
function MemberTable(props: {
  orgId: string;
  members: readonly Member[];
  actorRole: OrgRole;
  meOwnerId: string;
  onChanged: () => Promise<void>;
  navigate: (to: string) => void;
}): React.ReactElement {
  const [failure, setFailure] = useState<unknown>(null);
  const [confirming, setConfirming] = useState<Member | null>(null);
  /**
   * A role the caller picked for their own row, held until confirmed (LGN-128).
   *
   * Choosing Member on your own row used to strip your own permissions on the
   * selection, with no dialog, while *removing* the same person was behind
   * one. Demotion is the harder of the two to undo: it takes somebody else
   * with a managing role to put back.
   */
  const [confirmingRole, setConfirmingRole] = useState<{
    member: Member;
    role: OrgRole;
  } | null>(null);

  const changeRole = useMutation({
    mutationFn: (input: { ownerId: string; role: OrgRole }) =>
      api.changeMemberRole(props.orgId, input.ownerId, input.role),
    onSuccess: async () => {
      setFailure(null);
      setConfirmingRole(null);
      await props.onChanged();
    },
    onError: (error: unknown) => {
      setConfirmingRole(null);
      setFailure(error);
    },
  });

  const remove = useMutation({
    mutationFn: (ownerId: string) => api.removeMember(props.orgId, ownerId),
    onSuccess: async (_result, ownerId) => {
      setFailure(null);
      setConfirming(null);
      await props.onChanged();
      // Removing yourself ends your access to this org, so the switcher's
      // choice is now stale. Back to the sites screen, which re-picks.
      if (ownerId === props.meOwnerId) props.navigate("/");
    },
    onError: (error: unknown) => {
      setConfirming(null);
      setFailure(error);
    },
  });

  const managing = canManageMembers(props.actorRole);
  const options = assignableRoles(props.actorRole);
  const { actorRole, meOwnerId } = props;
  const roleChanging = changeRole.isPending;
  const setRole = changeRole.mutate;

  const columns = useMemo(
    () =>
      memberColumns({
        managing,
        actorRole,
        meOwnerId,
        options,
        roleChanging,
        setRole,
        setConfirming,
        setConfirmingRole,
      }),
    [managing, actorRole, meOwnerId, options, roleChanging, setRole],
  );

  const leaving = confirming !== null && confirming.ownerId === props.meOwnerId;

  return (
    <div className="lg:flex lg:flex-col lg:gap-3">
      {failure === null ? null : (
        <Failure error={failure} fallback="That change did not go through." />
      )}

      <DataTable caption="Members of this organisation" columns={columns} data={props.members} />

      {/*
        `window.confirm` before this: a browser dialog that could not say what
        it was about in the product's own words, could not be styled, and on
        some platforms is suppressed entirely. Same question, same two answers.
      */}
      <ConfirmDialog
        open={confirming !== null}
        onOpenChange={(next) => {
          if (!next) setConfirming(null);
        }}
        title={leaving ? "Leave this organisation?" : "Remove this person?"}
        description={
          leaving
            ? "You will lose access to its sites."
            : `${confirming?.email ?? ""} loses access to this organisation's sites.`
        }
        confirmLabel={leaving ? "Leave" : "Remove"}
        destructive
        pending={remove.isPending}
        onConfirm={() => {
          if (confirming !== null) remove.mutate(confirming.ownerId);
        }}
      />

      {/*
        The same dialog in front of a self-demotion, worded as what it costs
        rather than as the role's name. Only for your own row: changing
        somebody else's role is reversible by the person doing it.
      */}
      <ConfirmDialog
        open={confirmingRole !== null}
        onOpenChange={(next) => {
          if (!next) setConfirmingRole(null);
        }}
        title="Change your own role?"
        description={
          confirmingRole === null ? undefined : describeSelfRoleChange(confirmingRole.role)
        }
        confirmLabel={
          confirmingRole === null ? "Change my role" : `Make me ${ROLE_LABELS[confirmingRole.role]}`
        }
        destructive
        pending={changeRole.isPending}
        onConfirm={() => {
          if (confirmingRole !== null) {
            changeRole.mutate({
              ownerId: confirmingRole.member.ownerId,
              role: confirmingRole.role,
            });
          }
        }}
      />
    </div>
  );
}

/**
 * What a change to your own role costs you, in the words of what is lost.
 *
 * Not "you will be a Member": the role name is exactly the part a person
 * choosing it already knows. What they do not necessarily know is that the
 * choice takes away the control that would let them undo it.
 */
function describeSelfRoleChange(next: OrgRole): string {
  if (next === "billing") {
    return "You lose access to this organisation's sites, and the ability to invite people or change roles. Only an owner or admin can give it back to you.";
  }
  if (!canManageMembers(next)) {
    return "You lose the ability to invite people, change roles and manage this organisation, including the ability to change this back. Only an owner or admin can give it back to you.";
  }
  if (!canManageOwners(next)) {
    return "You lose the ability to add or remove an owner, including your own place as one. Only an owner can give it back to you.";
  }
  return "Your own role in this organisation changes straight away.";
}

/** Invitations sent and not yet answered, with the way to withdraw one. */
function PendingInvitations(props: {
  orgId: string;
  query: {
    isPending: boolean;
    isError: boolean;
    error: unknown;
    data: { invitations: Invitation[] } | undefined;
  };
  onChanged: () => Promise<void>;
}): React.ReactElement {
  const revoke = useMutation({
    mutationFn: (invitationId: string) => api.revokeInvitation(props.orgId, invitationId),
    onSuccess: () => props.onChanged(),
  });

  const revoking = revoke.isPending;
  const revokeInvitation = revoke.mutate;
  const columns = useMemo(
    () => invitationColumns(revokeInvitation, revoking),
    [revokeInvitation, revoking],
  );

  return (
    <section className="lg:flex lg:flex-col lg:gap-4">
      <H2 className="lg:text-h3">Pending invitations</H2>

      {props.query.isError ? (
        <Failure error={props.query.error} fallback="Could not load the pending invitations." />
      ) : (
        <DataTable
          caption="Invitations sent and not yet answered"
          columns={columns}
          data={props.query.data?.invitations ?? []}
          loading={props.query.isPending}
          loadingRows={2}
          empty={
            <EmptyState
              glow={false}
              icon={<MailCheck />}
              title="Nobody is waiting on an invitation"
              description="Invite somebody above and they appear here until they accept."
            />
          }
        />
      )}

      {revoke.isError ? (
        <Failure error={revoke.error} fallback="Could not withdraw that invitation." />
      ) : null}
    </section>
  );
}

/**
 * Inviting somebody.
 *
 * The link goes out by email and appears nowhere here, which is the whole
 * design: the platform returns the token exactly once, into the message body.
 * When the send fails the invitation still exists, so the notice says so rather
 * than claiming success, and the remedy is to withdraw it and try again.
 */
function InviteForm(props: { orgId: string; onInvited: () => Promise<void> }): React.ReactElement {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<OrgRole>("member");

  const invite = useMutation({
    mutationFn: () => api.invite(props.orgId, email.trim(), role),
    onSuccess: async () => {
      setEmail("");
      await props.onInvited();
    },
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    invite.mutate();
  }

  // Never `owner`: the schema's CHECK refuses an invitation carrying it, and
  // ownership moves by promoting somebody who is already in the org.
  const options: OrgRole[] = ["admin", "member", "billing"];

  return (
    <Card as="form" interactive={false} onSubmit={submit}>
      <div className="lg:flex lg:flex-col lg:gap-5">
        <CardHeader>
          <CardTitle as="h2">Invite somebody</CardTitle>
        </CardHeader>

        {invite.isError ? (
          <Failure error={invite.error} fallback="Could not send that invitation." />
        ) : invite.isSuccess ? (
          <Notice tone={invite.data.emailed ? "success" : "danger"}>
            {invite.data.emailed
              ? `Invitation sent to ${invite.data.email}. It expires ${new Date(
                  invite.data.expiresAt,
                ).toLocaleDateString()}.`
              : `The invitation for ${invite.data.email} was created, but the email could not be sent. Withdraw it below and try again.`}
          </Notice>
        ) : null}

        <div className="lg:flex lg:flex-wrap lg:items-end lg:gap-4">
          <FormField label="Email address" id="invite-email" className="lg:min-w-56 lg:flex-1">
            <Input
              type="email"
              value={email}
              maxLength={320}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              placeholder="colleague@example.com"
              required
            />
          </FormField>
          <SelectField
            id="invite-role"
            label="Role"
            className="lg:w-44"
            value={role}
            onValueChange={(value) => {
              setRole(parseOrgRole(value) ?? "member");
            }}
          >
            {options.map((option) => (
              <SelectItem key={option} value={option}>
                {ROLE_LABELS[option]}
              </SelectItem>
            ))}
          </SelectField>
          <Button type="submit" disabled={invite.isPending || email.trim().length === 0}>
            {invite.isPending ? "Sending…" : "Send invitation"}
          </Button>
        </div>

        <Text size="caption" tone="subtle">
          {ROLE_DESCRIPTIONS[role]}
        </Text>
      </div>
    </Card>
  );
}

/**
 * Making a second organisation.
 *
 * Here rather than in the switcher, because "create a shared space and put
 * people in it" is the same job as the rest of this screen, and a picker that
 * also creates things is a picker that surprises somebody scrolling it with a
 * keyboard.
 */
function NewOrganisation(props: { onCreated: (orgId: string) => void }): React.ReactElement {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  const create = useMutation({
    mutationFn: () => api.createOrg(name.trim()),
    onSuccess: async (org) => {
      await queryClient.invalidateQueries({ queryKey: ["orgs"] });
      setOpen(false);
      setName("");
      props.onCreated(org.id);
    },
  });

  if (!open) {
    return (
      <section className="lg:flex lg:flex-col lg:items-start lg:gap-3">
        <H2 className="lg:text-h3">Another organisation</H2>
        <Text size="sm" tone="muted">
          A separate space with its own sites, people and bill. You will be its owner.
        </Text>
        <Button
          variant="outline"
          onClick={() => {
            setOpen(true);
          }}
        >
          New organisation
        </Button>
      </section>
    );
  }

  return (
    <Card
      as="form"
      interactive={false}
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        create.mutate();
      }}
    >
      <div className="lg:flex lg:flex-col lg:gap-5">
        <CardHeader>
          <CardTitle as="h2">New organisation</CardTitle>
          <CardDescription>
            A separate space with its own sites, people and bill. You will be its owner.
          </CardDescription>
        </CardHeader>

        {create.isError ? (
          <Failure error={create.error} fallback="Could not create the organisation." />
        ) : null}

        <FormField label="Name" id="new-org-name" required>
          <Input
            value={name}
            autoFocus
            maxLength={100}
            onChange={(event) => {
              setName(event.target.value);
            }}
            placeholder="Acme Ltd"
            required
          />
        </FormField>

        <div className="lg:flex lg:flex-wrap lg:gap-3">
          <Button type="submit" disabled={create.isPending || name.trim().length === 0}>
            {create.isPending ? "Creating…" : "Create organisation"}
          </Button>
          <Button
            variant="ghost"
            type="button"
            onClick={() => {
              setOpen(false);
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Card>
  );
}

/**
 * The member columns, at module scope.
 *
 * A `cell` renderer returns JSX and therefore reads as a component to a linter
 * inside a component body, and rebuilding the definitions on every render is
 * the thing TanStack asks you not to do. Everything they close over arrives as
 * one argument bag instead.
 */
function memberColumns(input: {
  managing: boolean;
  actorRole: OrgRole;
  meOwnerId: string;
  options: readonly OrgRole[];
  roleChanging: boolean;
  setRole: (input: { ownerId: string; role: OrgRole }) => void;
  setConfirming: (member: Member) => void;
  setConfirmingRole: (choice: { member: Member; role: OrgRole }) => void;
}): DataTableColumn<Member>[] {
  // An admin may not touch an owner at all: the server refuses both the
  // demotion and the removal, so neither control is offered.
  const reachable = (member: Member): boolean =>
    input.managing && (member.role !== "owner" || canManageOwners(input.actorRole));

  return [
    {
      accessorKey: "email",
      header: "Person",
      cell: (info) => {
        const member = info.row.original;
        return (
          <span className="lg:flex lg:flex-col">
            <span className="lg:text-strong">{member.name ?? member.email}</span>
            {member.name === null ? null : (
              <Text as="span" size="caption" tone="subtle">
                {member.email}
              </Text>
            )}
          </span>
        );
      },
    },
    {
      accessorKey: "role",
      header: "Role",
      cell: (info) => {
        const member = info.row.original;
        if (!reachable(member)) return <Badge tone="neutral">{ROLE_LABELS[member.role]}</Badge>;
        return (
          <Select
            value={member.role}
            disabled={input.roleChanging}
            onValueChange={(value) => {
              const next = parseOrgRole(value);
              if (next === undefined || next === member.role) return;
              // Your own row goes through the dialog; everybody else's does
              // not, because the person pressing it can put that one back.
              if (member.ownerId === input.meOwnerId) {
                input.setConfirmingRole({ member, role: next });
                return;
              }
              input.setRole({ ownerId: member.ownerId, role: next });
            }}
          >
            <SelectTrigger aria-label={`Role for ${member.email}`} className="lg:w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {input.options.map((role) => (
                <SelectItem key={role} value={role}>
                  {ROLE_LABELS[role]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      },
    },
    {
      accessorKey: "joinedAt",
      header: "Joined",
      sortFn: "datetime",
      cell: (info) => (
        <Text as="span" size="caption" tone="muted">
          {new Date(info.row.original.joinedAt).toLocaleDateString()}
        </Text>
      ),
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: (info) => {
        const member = info.row.original;
        const isMe = member.ownerId === input.meOwnerId;
        // Anybody may leave; removing somebody else needs a managing role.
        if (!isMe && !reachable(member)) return null;
        return (
          <span className="lg:flex lg:justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                input.setConfirming(member);
              }}
            >
              {isMe ? "Leave" : "Remove"}
            </Button>
          </span>
        );
      },
    },
  ];
}

/** The pending-invitation columns, at module scope for the same reason. */
function invitationColumns(
  revokeInvitation: (invitationId: string) => void,
  revoking: boolean,
): DataTableColumn<Invitation>[] {
  return [
    { accessorKey: "email", header: "Address" },
    {
      accessorKey: "role",
      header: "Role",
      cell: (info) => <Badge tone="neutral">{ROLE_LABELS[info.row.original.role]}</Badge>,
    },
    {
      accessorKey: "expiresAt",
      header: "Expires",
      sortFn: "datetime",
      cell: (info) => (
        <Text as="span" size="caption" tone="muted">
          {new Date(info.row.original.expiresAt).toLocaleDateString()}
        </Text>
      ),
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: (info) => (
        <span className="lg:flex lg:justify-end">
          <Button
            variant="ghost"
            size="sm"
            disabled={revoking}
            onClick={() => {
              revokeInvitation(info.row.original.id);
            }}
          >
            Withdraw
          </Button>
        </span>
      ),
    },
  ];
}
